import { Box, Typography } from '@mui/material'
import { Countries } from '@xoda/common'
import dayjs from 'dayjs'
import i18next from 'i18next'
import { confirm } from 'material-ui-confirm'
import toast from 'react-hot-toast'
import { snapshot_UNSTABLE } from 'recoil'
import { Api } from '../api'
import { SessionSummary } from '../components/SessionSummary'
import { gymState } from '../store/gym'
import { userState } from '../store/user'

class SessionService {
  static isFull(session: Dto.MarketSession) {
    return session.confirmed_bookings_count >= session.member_limit
  }

  static isLateCancellation(session: Dto.Session) {
    const diff = dayjs(session.session_start_time_utc).diff(dayjs(), 'minute')
    return diff <= session.lockout_period
  }

  static async cancelBooking(
    booking: Dto.Booking,
    session: Dto.Session,
    {
      onBeforeRequest,
    }: Partial<{
      onBeforeRequest: VoidFunction
    }> = {}
  ) {
    const config = booking.is_confirmed
      ? {
          title: i18next.t('Cancel booking'),
          alert: [
            i18next.t('Are you sure you want to cancel this booking?'),
            ...(session.penalty_fee &&
            SessionService.isLateCancellation(session)
              ? [
                  i18next.t(
                    `A late cancellation fee of {{amount}} will apply.`,
                    {
                      amount: Countries.formatAmount(session.penalty_fee),
                    }
                  ),
                ]
              : []),
          ].join(' '),
          cancel: i18next.t('Keep booking'),
          success: i18next.t('Your booking has been cancelled.'),
        }
      : {
          title: i18next.t('Cancel waitlist'),
          alert: i18next.t(
            'Are you sure you want to cancel your waitlist spot?'
          ),
          cancel: i18next.t('Stay on waitlist'),
          success: i18next.t('Your waitlist spot has been cancelled.'),
        }

    if (
      !(
        await confirm({
          title: config.title,
          content: (
            <>
              <Typography variant="h4">{config.alert}</Typography>
              <Box mt={2}>
                <SessionSummary session={session} name wrapped />
              </Box>
            </>
          ),
          confirmationText: config.title,
          cancellationText: config.cancel,
          dialogProps: { maxWidth: 'xs', fullWidth: true },
        })
      ).confirmed
    ) {
      return false
    }

    onBeforeRequest?.()
    await Api.put(`bookings/cancel/${booking.id!}`, undefined, {
      memberAuth: true,
    })
    toast.success(config.success)
    return true
  }

  static async book(sessionId: number, { unpaid = false } = {}) {
    return await Api.post(
      'bookings',
      {
        session_id: sessionId,
        ...(unpaid && { booking_with_advanced_credits: true }),
      },
      { memberAuth: true }
    )
  }

  static async pay(session: Dto.Session) {
    const booking = await this.book(session.id, { unpaid: true })
    const user = snapshot_UNSTABLE().getLoadable(userState).contents
    const gym = snapshot_UNSTABLE().getLoadable(gymState).contents
    await Api.post(
      'purchases',
      {
        pos_items: [
          {
            type: 'credit',
            name: session.gym_category.category.name,
            price: session.gym_category.credit_fee,
            quantity: 1,
            booking_id: booking.id,
          },
        ],
        user_id: user.userId,
        payment_method: gym.payment_method,
        gym_id: gym.id,
      },
      { memberAuth: true }
    )
  }
}

export { SessionService }
